import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaTimes } from 'react-icons/fa';

/**
 * Édition d'une liste de compétences ou de langues du profil enrichi, dans la
 * modale « Modifier mon profil » (YRH-NEW-008-T5, Yros/Kavero#481).
 *
 * Le brouillon appartient à la modale : ce composant l'affiche en pastilles
 * « nom · niveau » avec un × pour retirer, et annonce chaque changement par
 * `onChange`. Il n'a ni Enregistrer ni Annuler : la modale n'en a qu'un. La
 * ligne de saisie n'apparaît qu'au clic sur « + Ajouter ».
 *
 * Les langues sont une liste fermée : on choisit dans le catalogue. Les
 * compétences se tapent librement (`libre`) ; le nom tapé est rapproché du
 * catalogue sans tenir compte de la casse ni des espaces, et envoyé par son
 * identifiant s'il existe, par son nom sinon.
 */

const NOM_MAX = 60;
const LIGNES_MAX = 50;

const normaliser = (texte) => texte.trim().replace(/\s+/g, ' ');
const cle = (texte) => normaliser(texte).toLocaleLowerCase();

export const versBrouillon = (lignes) =>
  (lignes || []).map((ligne) => ({
    skill_id: ligne.skill_id,
    nom: ligne.competence,
    niveau_id: ligne.niveau_id,
    niveau: ligne.niveau,
  }));

// Le corps attendu par POST /api/rh/mon-profil : des identifiants pour une
// compétence connue, un nom pour une nouvelle.
export const versCorps = (brouillon) =>
  brouillon.map((l) => (l.skill_id ? { skill_id: l.skill_id, niveau_id: l.niveau_id } : { nom: l.nom, niveau_id: l.niveau_id }));

// Ce qui compte pour savoir si un brouillon a changé : la compétence et son
// niveau, jamais l'identité des tableaux (chaque lecture du profil en crée de
// nouveaux).
const signature = (liste) => JSON.stringify(liste.map((l) => [l.skill_id || cle(l.nom), l.niveau_id]));
export const memeContenu = (a, b) => signature(a) === signature(b);

function EditionCompetences({ prefixe, titre, libelleAjout, lignes, catalogue, libre, enCours, onChange }) {
  const { t } = useTranslation();
  const [ouverte, setOuverte] = useState(false);
  const [choix, setChoix] = useState('');
  const [niveauId, setNiveauId] = useState('');
  const [message, setMessage] = useState(null);

  const niveaux = (catalogue && catalogue.niveaux) || [];
  const connues = (catalogue && catalogue.competences) || [];
  // Rien à proposer : pas de niveau, ou une liste fermée sans aucune entrée.
  // On le dit, au lieu d'offrir un sélecteur vide.
  const catalogueVide = niveaux.length === 0 || (!libre && connues.length === 0);
  const nomSaisi = libre ? normaliser(choix) : '';
  const peutAjouter =
    !enCours &&
    lignes.length < LIGNES_MAX &&
    niveauId !== '' &&
    (libre ? nomSaisi !== '' && nomSaisi.length <= NOM_MAX : choix !== '');

  const dejaPresente = (skillId, nom) =>
    lignes.some((l) => (skillId && l.skill_id === skillId) || cle(l.nom) === cle(nom));

  const fermer = () => {
    setOuverte(false);
    setChoix('');
    setNiveauId('');
    setMessage(null);
  };

  const ajouter = () => {
    const niveau = niveaux.find((n) => String(n.id) === niveauId);
    if (!niveau) return;
    // Une compétence tapée à la casse près d'une compétence connue prend son
    // identifiant et son nom : le catalogue ne grossit pas de doublons.
    const connue = libre
      ? connues.find((c) => cle(c.nom) === cle(nomSaisi))
      : connues.find((c) => String(c.id) === choix);
    if (!libre && !connue) return;
    const nom = connue ? connue.nom : nomSaisi;
    if (dejaPresente(connue && connue.id, nom)) {
      setMessage(t('monProfil.dejaPresente'));
      return;
    }
    onChange([...lignes, { skill_id: connue && connue.id, nom, niveau_id: niveau.id, niveau: niveau.nom }]);
    fermer();
  };

  const retirer = (index) => onChange(lignes.filter((_, i) => i !== index));

  const listeId = `liste-${prefixe}`;

  return (
    <div data-testid={`edition-${prefixe}`} className="space-y-2">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{titre}</p>

      {lignes.length ? (
        <ul className="flex flex-wrap gap-1.5">
          {lignes.map((ligne, index) => (
            <li
              key={`${ligne.skill_id || ligne.nom}`}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium"
            >
              <span>{`${ligne.nom} · ${ligne.niveau}`}</span>
              <button
                type="button"
                data-testid={`retirer-${prefixe}-${index}`}
                aria-label={`${t('monProfil.retirer')} ${ligne.nom}`}
                disabled={enCours}
                onClick={() => retirer(index)}
                className="p-0.5 rounded-full text-slate-400 hover:text-red-600 disabled:opacity-50"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs sm:text-sm text-slate-400">{'—'}</p>
      )}

      {catalogueVide ? (
        <p data-testid={`catalogue-vide-${prefixe}`} className="text-xs text-slate-500">
          {t('monProfil.catalogueVide')}
        </p>
      ) : (
        <>
          <button
            type="button"
            data-testid={`ouvrir-ajout-${prefixe}`}
            disabled={enCours || lignes.length >= LIGNES_MAX}
            onClick={() => (ouverte ? fermer() : setOuverte(true))}
            className="inline-flex items-center gap-1.5 text-sm text-[--color-primary] hover:underline disabled:opacity-50 disabled:no-underline"
          >
            <FaPlus className="w-3 h-3" />
            {libelleAjout}
          </button>

          {ouverte && (
            <div className="flex flex-col gap-2">
              {libre ? (
                <>
                  <input
                    type="text"
                    data-testid={`ajout-${prefixe}-choix`}
                    list={listeId}
                    value={choix}
                    maxLength={NOM_MAX}
                    placeholder={t('monProfil.saisirCompetence')}
                    aria-label={t('monProfil.saisirCompetence')}
                    onChange={(e) => setChoix(e.target.value)}
                    className="w-full min-w-0 text-sm rounded-lg border border-slate-200 p-2 focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                  />
                  <datalist id={listeId}>
                    {connues.map((c) => (
                      <option key={c.id} value={c.nom} />
                    ))}
                  </datalist>
                </>
              ) : (
                <select
                  data-testid={`ajout-${prefixe}-choix`}
                  value={choix}
                  aria-label={t('monProfil.choisirLangue')}
                  onChange={(e) => setChoix(e.target.value)}
                  className="w-full min-w-0 text-sm rounded-lg border border-slate-200 p-2 bg-white"
                >
                  <option value="">{t('monProfil.choisirLangue')}</option>
                  {connues.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.nom}
                    </option>
                  ))}
                </select>
              )}
              <div className="flex gap-2">
                <select
                  data-testid={`ajout-${prefixe}-niveau`}
                  value={niveauId}
                  aria-label={t('monProfil.niveau')}
                  onChange={(e) => setNiveauId(e.target.value)}
                  className="flex-1 min-w-0 text-sm rounded-lg border border-slate-200 p-2 bg-white"
                >
                  <option value="">{t('monProfil.niveau')}</option>
                  {niveaux.map((n) => (
                    <option key={n.id} value={String(n.id)}>
                      {n.nom}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid={`ajout-${prefixe}-bouton`}
                  disabled={!peutAjouter}
                  onClick={ajouter}
                  className="shrink-0 px-3 py-2 text-sm rounded-lg text-[--color-primary] border border-[--color-primary] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {t('monProfil.ajouter')}
                </button>
                <button
                  type="button"
                  data-testid={`fermer-ajout-${prefixe}`}
                  aria-label={t('monProfil.fermer')}
                  onClick={fermer}
                  className="shrink-0 px-2 text-slate-400 hover:text-slate-600"
                >
                  <FaTimes className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {message && (
        <p data-testid={`edition-${prefixe}-message`} className="text-xs text-amber-700">
          {message}
        </p>
      )}
    </div>
  );
}

export default EditionCompetences;
